import { CircleAlert } from 'lucide-react';
import { forwardRef, useId, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Visible label (13px / 500). */
  label?: ReactNode;
  /** Muted helper text under the control. */
  hint?: ReactNode;
  /** Error message (replaces nothing — shown under the control with an alert icon). */
  error?: ReactNode;
  /** Shows the red asterisk after the label. */
  required?: boolean;
  /** Muted tag after the label, e.g. "optional". */
  optional?: ReactNode;
  disabled?: boolean;
  /** Id of the control; generated when omitted. */
  htmlFor?: string;
  /** Render the label as a plain element (for groups that label themselves with aria-labelledby). */
  labelAs?: 'label' | 'span';
  children: (ids: FieldIds) => ReactNode;
}

export interface FieldIds {
  controlId: string;
  labelId: string;
  hintId: string | undefined;
  errorId: string | undefined;
  /** Space-separated ids for aria-describedby (hint + error), or undefined. */
  describedBy: string | undefined;
}

/**
 * Label + control + hint/error layout shared by Input, Textarea, Select, Combobox and MultiSelect.
 * `children` is a render function that receives the ids to wire ARIA attributes.
 */
export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  {
    label,
    hint,
    error,
    required,
    optional,
    disabled,
    htmlFor,
    labelAs = 'label',
    className,
    children,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const controlId = htmlFor ?? `f${autoId}`;
  const labelId = `${controlId}-label`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const LabelTag = labelAs;

  return (
    <div ref={ref} className={cn('flex min-w-0 flex-col gap-1.5', className)} {...rest}>
      {label ? (
        <LabelTag
          id={labelId}
          {...(labelAs === 'label' ? { htmlFor: controlId } : {})}
          className={cn('text-13 font-medium', disabled ? 'text-text-3' : 'text-text')}
        >
          {label}
          {required ? (
            <span className="text-error" aria-hidden>
              {' '}
              *
            </span>
          ) : null}
          {optional ? <span className="font-normal text-text-3"> {optional}</span> : null}
        </LabelTag>
      ) : null}
      {children({ controlId, labelId, hintId, errorId, describedBy })}
      {error ? (
        <span id={errorId} className="flex items-center gap-1.5 text-12 text-error-text">
          <CircleAlert aria-hidden className="size-3 shrink-0" strokeWidth={2.5} />
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={hintId} className="text-12 text-text-3">
          {hint}
        </span>
      ) : null}
    </div>
  );
});
